import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  GET_HAND_HISTORY,
  GET_OWN_HAND_CARDS,
  GOT_HAND_HISTORY,
  GOT_OWN_HAND_CARDS
} from 'constants/eventConstants';
import { GAME_HAND_PHASE, PLAYER_STATUS } from 'constants/gameConstants';
import { useHandHistoryNativeCheckpoint } from 'hooks/useHandHistoryNativeCheckpoint';
import { useTableSelector } from 'hooks/useTableSelector';
import { computeHandHistoryPlayerId } from 'utils/handHistoryPlayerId';
import {
  clearHandHistorySessionData,
  clearLegacyHandHistoryStorage,
  endHandHistorySession,
  freezeHandHistorySession,
  hasEndedHandHistorySession,
  isHandHistorySessionEndStatus,
  isHandHistorySessionExitStatus,
  isHandHistorySessionFreezeStatus,
  pauseHandHistorySession,
  persistHandHistoryPanelOpen,
  persistHandHistorySession,
  persistHandHistorySessionData,
  qualifyHandHistorySession,
  readHandHistoryExcludedGameNos,
  readHandHistoryFirstGameNo,
  readHandHistoryFrozenThroughGameNo,
  readHandHistoryPanelOpen,
  readHandHistorySessionData,
  readHandHistorySessionStartedAt,
  resolveHandHistorySessionStartedAt
} from 'utils/handHistorySession';
import { GLOBAL_DATA_CDN_URL } from 'utils/urls';

import {
  HandHistoryDemoGameMode,
  HandHistoryDemoVariant,
  handHistoryScenarios,
  lockedHandHistoryFixture
} from 'components/HandHistory/__fixtures__/handHistory.fixtures';
import { getLiveRunBoards } from 'components/HandHistory/liveRunBoards';
import { dealtCards, isStreetText } from 'components/HandHistory/playByPlayRounds';
import {
  ActiveHand,
  HandHistoryEvent,
  HandHistoryResponse,
  HandHistorySnapshot
} from 'components/HandHistory/types';

import { DualSocketContext } from './DualSocketContext';
import { decryptHistoryDocument, requestHandGrants } from './encryptedHandHistory';
import { createOwnHandCardsRecovery } from './ownHandCardsRecovery';

export interface HandHistoryContextValue {
  data: HandHistoryResponse;
  tableScope?: string;
  isLoading: boolean;
  isPreparing?: boolean;
  error?: string;
  refresh: () => void;
  demoLocked: boolean;
  setDemoLocked: React.Dispatch<React.SetStateAction<boolean>>;
  demoGameMode: HandHistoryDemoGameMode;
  setDemoGameMode: React.Dispatch<React.SetStateAction<HandHistoryDemoGameMode>>;
  demoVariant: HandHistoryDemoVariant;
  setDemoVariant: React.Dispatch<React.SetStateAction<HandHistoryDemoVariant>>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  revealOwnCards: (gameNo: number, completedAt: number) => void;
}

export const HandHistoryContext = createContext<HandHistoryContextValue | undefined>(undefined);

type HandHistorySource = 'live' | 'demo';

const liveEmptyHandHistory: HandHistoryResponse = {
  locked: true,
  completedHands: []
};

// The authenticated WebSocket fallback has no transport-level acknowledgement.
// Bound it so a lost reply ends in the panel's error/Retry state, not a spinner.
export const HAND_HISTORY_FALLBACK_TIMEOUT_MS = 10000;

const logHandHistoryDiagnostic = (
  level: 'info' | 'warn',
  message: string,
  details: Record<string, unknown>
) => {
  // eslint-disable-next-line no-console
  console[level](`[HandHistory] ${message}`, details);
};

export {
  getHandHistorySessionKey,
  readHandHistorySessionStartedAt,
  resolveHandHistorySessionStartedAt
} from 'utils/handHistorySession';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const gameMarkers = (tableId: string, gameNo: number | string) => ({
  current: new RegExp(`^Game #${escapeRegExp(String(tableId))}-${escapeRegExp(String(gameNo))}\\b`),
  any: new RegExp(`^Game #${escapeRegExp(String(tableId))}-\\d+\\b`)
});

const entryMessage = (entry: any) => String(entry?.msg || '').trim();

/**
 * This hand's rows from the first one on, ending before the next hand's
 * marker, with any repeat of this hand's own marker dropped.
 *
 * The marker can repeat inside a hand: between the boards of a multi-run hand
 * the engine writes GAME_MSG_PROCESSING ("Game ") + "#table-gameNo" with the
 * SAME gameNo (holdem/omaha engine.js case "14", RunMultiple branch). That row
 * is a processing beat, not a new hand: anchoring on it threw away everything
 * before run 2, run 1's board and payouts included.
 */
const boundCurrentHand = (entries: any[], markers: ReturnType<typeof gameMarkers>) => {
  // The next hand's marker can arrive before gameNo advances. Keep that
  // marker (and subsequent rows) out of the hand that is finishing.
  const nextHandIndex = entries.findIndex((entry) => {
    const message = entryMessage(entry);
    return markers.any.test(message) && !markers.current.test(message);
  });
  const bounded = nextHandIndex < 0 ? entries : entries.slice(0, nextHandIndex);
  return bounded.filter(
    (entry, index) => index === 0 || !markers.current.test(entryMessage(entry))
  );
};

export const getCurrentHandHistoryEntries = (
  tableHistory: any[] = [],
  tableId: string,
  gameNo: number | string | undefined,
  fallbackStartIndex?: number
) => {
  if (!tableId || gameNo === undefined || gameNo === null || gameNo === '') return [];

  const markers = gameMarkers(tableId, gameNo);
  // This hand starts at the first of its latest run of markers: walking back,
  // a later hand's marker (which can arrive before gameNo advances) is passed
  // over, and an earlier hand's marker ends the walk.
  let startIndex = -1;
  for (let index = tableHistory.length - 1; index >= 0; index -= 1) {
    const message = entryMessage(tableHistory[index]);
    if (markers.current.test(message)) startIndex = index;
    else if (startIndex >= 0 && markers.any.test(message)) break;
  }

  if (startIndex < 0) {
    if (fallbackStartIndex === undefined) return [];
    startIndex = Math.max(0, Math.min(fallbackStartIndex, tableHistory.length));
  }

  return boundCurrentHand(tableHistory.slice(startIndex), markers);
};

export const getHandHistoryEntriesForGame = (
  tableHistory: any[] = [],
  tableId: string,
  gameNo: number | string | undefined
) => {
  if (!tableId || gameNo === undefined || gameNo === null || gameNo === '') return [];

  const markers = gameMarkers(tableId, gameNo);
  const startIndex = tableHistory.findIndex((entry) => markers.current.test(entryMessage(entry)));
  if (startIndex < 0) return [];

  return boundCurrentHand(tableHistory.slice(startIndex), markers);
};

// The engine's street rows ("Dealing Flop" / "Dealing Turn" / "Dealing River",
// and the broadcaster's "Run n" board row on a multi-run hand) are ordinary
// system rows in the live table log that happen to carry the board
// (`card1`..`card5`, undealt slots empty). `isShowCard` marks something else:
// the showdown rows that carry the players' shown hole cards. The capture
// (shared/handHistorySnapshot/builder.js) types streets by their wording, and
// so does this now, so the live card draws the same street headers as a
// completed hand. A showdown row is never a street, whatever it carries: the
// broadcaster attaches no board to one today, but if it ever did, each would
// open its own River container.
const liveEventType = (entry: any) => {
  if (isStreetText(String(entry?.msg || ''))) return 'street';
  if (entry?.isShowCard) return 'showdown';
  return entry?.systemMsg ? 'system' : 'action';
};

export const buildHandHistoryEvents = (
  historyEntries: any[] = [],
  tableId: string,
  gameNo: number | string | undefined,
  scope: 'active' | 'completed' = 'active'
): HandHistoryEvent[] =>
  historyEntries
    .filter((entry: any) => entry?.msg || entry?.isShowCard)
    .map((entry: any, index: number) => {
      const dealt = dealtCards(entry.tableCards);
      return {
        id: `${tableId}-${gameNo || 0}-${scope}-${index}`,
        order: index + 1,
        type: liveEventType(entry),
        text: entry.msg || '',
        ...(entry.ts ? { ts: entry.ts } : {}),
        ...(entry.totalPot !== null && entry.totalPot !== undefined
          ? { totalPot: Number(entry.totalPot) }
          : {}),
        ...(dealt.length > 0 ? { tableCards: dealt } : {})
      };
    });

export const enrichCompletedHandPlayByPlay = (
  hands: HandHistorySnapshot[] = [],
  tableHistory: any[] = [],
  tableId: string,
  capturedActiveHands: ActiveHand[] = []
) =>
  hands.map((hand) => {
    if (hand.playByPlayComplete) return hand;
    const localEvents = buildHandHistoryEvents(
      getHandHistoryEntriesForGame(tableHistory, tableId, hand.gameNo),
      tableId,
      hand.gameNo,
      'completed'
    );
    const capturedEvents =
      capturedActiveHands.find((activeHand) => Number(activeHand.gameNo) === Number(hand.gameNo))
        ?.playByPlay || [];
    const pageEvents = capturedEvents.length > localEvents.length ? capturedEvents : localEvents;
    const serverEvents = hand.playByPlay || [];
    const serverHasOnlyTerminalSummary =
      serverEvents.length > 0 && serverEvents.every((event) => event.type === 'summary');

    if (
      pageEvents.length === 0 ||
      (!serverHasOnlyTerminalSummary && pageEvents.length <= serverEvents.length)
    ) {
      return hand;
    }

    return {
      ...hand,
      playByPlay: pageEvents.map((event, index) => ({
        ...event,
        id: `${hand.handId}-completed-${index}`,
        order: index + 1
      }))
    };
  });

export const attachViewerSeatToCompletedHands = (
  hands: HandHistorySnapshot[] = [],
  playerGUID: string
) => {
  const viewerId = String(playerGUID || '').trim();
  if (!viewerId) return hands;

  return hands.map((hand) => {
    // The document never carries the raw GUID (see toPublicHandHistorySnapshot
    // in serverless-v2/shared/handHistorySnapshot/builder.js) - every seat's
    // playerId is a pseudonymous digest keyed by this hand's gameId. Recompute
    // the identical digest from the authenticated GUID we already have to find
    // which seat is ours, rather than comparing against the raw value.
    const hashedViewerId = computeHandHistoryPlayerId(hand.gameId, viewerId);
    const matchedSeat = hand.tableSnapshot?.seats?.find(
      (seat) => String(seat.playerId || '').trim() === hashedViewerId
    )?.seat;
    const viewerSeat = matchedSeat ?? hand.tableSnapshot?.viewerSeat;
    if (!viewerSeat || hand.tableSnapshot.viewerSeat === viewerSeat) return hand;

    return {
      ...hand,
      tableSnapshot: {
        ...hand.tableSnapshot,
        viewerSeat
      }
    };
  });
};

/**
 * Patches in own cards lazily recovered from the `get_ownHandCards` backstop
 * (see ownCardsStore.js on the backend) - the shared document these hands
 * otherwise came from can never carry a player's own unshown cards. Keyed by
 * `gameNo` because that's what the backstop lookup is keyed by; requires
 * `attachViewerSeatToCompletedHands` to have already run so `viewerSeat` is
 * resolved.
 *
 * Patches both `tableSnapshot.seats` AND `potGroups[].players`. The display
 * layer (HandSnapshot's buildDisplayTableSnapshot) merges the two per seat
 * with `player.cards ?? seat.cards` / `player.cardsRevealed ?? seat.cardsRevealed`
 * - a folded player who contributed to any pot has a real, non-nullish
 * `cards: []` / `cardsRevealed: false` entry in potGroups from capture time,
 * so `??` never falls back to a seat-only patch. Both need the same cards.
 */
export const applyRevealedOwnCards = (
  hands: HandHistorySnapshot[] = [],
  revealedOwnCards: Record<number, string[]>
) => {
  if (!revealedOwnCards || Object.keys(revealedOwnCards).length === 0) return hands;

  return hands.map((hand) => {
    const holeCards = revealedOwnCards[Number(hand.gameNo)];
    const viewerSeat = hand.tableSnapshot?.viewerSeat;
    if (!holeCards || holeCards.length === 0 || !viewerSeat) return hand;

    const seats = hand.tableSnapshot?.seats?.map((seat) =>
      Number(seat.seat) === Number(viewerSeat)
        ? { ...seat, cards: holeCards, cardsRevealed: true }
        : seat
    );
    const potGroups = hand.potGroups?.map((pot) => ({
      ...pot,
      players: pot.players.map((player) =>
        Number(player.seat) === Number(viewerSeat)
          ? { ...player, cards: holeCards, cardsRevealed: true }
          : player
      )
    }));

    return {
      ...hand,
      ...(seats ? { tableSnapshot: { ...hand.tableSnapshot, seats } } : {}),
      ...(potGroups ? { potGroups } : {})
    };
  });
};

export const getNextActiveHistoryBaseline = (
  currentBaseline: { key: string; index: number },
  activeHandKey: string,
  previousTableHistoryLength: number
) => {
  if (currentBaseline.key === activeHandKey) return currentBaseline;

  return {
    key: activeHandKey,
    index: currentBaseline.key ? previousTableHistoryLength : 0
  };
};

const getRequestedTableId = (game: any) => (game?.gameID ? String(game.gameID) : '');

const getActiveHandKey = (requestedTableId: string, currentGameNo: number | string | undefined) =>
  requestedTableId && currentGameNo !== undefined && currentGameNo !== null && currentGameNo !== ''
    ? `${requestedTableId}-${currentGameNo}`
    : '';

export const isMatchingHandHistoryResponse = (
  payload: any,
  requestedTableId: string,
  requestId: string
) => {
  const responseTableId = payload?.tableId || payload?.gameId;
  return (
    Boolean(requestId) &&
    payload?.requestId === requestId &&
    Boolean(responseTableId) &&
    String(responseTableId) === requestedTableId
  );
};

const compareCompletedHandsNewestFirst = (left: HandHistorySnapshot, right: HandHistorySnapshot) =>
  Number(right.gameNo || 0) - Number(left.gameNo || 0) ||
  Number(right.completedAt || 0) - Number(left.completedAt || 0);

export const sortCompletedHandsNewestFirst = (hands: HandHistorySnapshot[] = []) =>
  [...hands].sort(compareCompletedHandsNewestFirst);

export const getCompletedHandSelectionKey = (hand: HandHistorySnapshot) => {
  const gameNo = Number(hand.gameNo);
  const tableIdentity = hand.tableId || hand.gameId;

  return Number.isFinite(gameNo) && gameNo > 0 && tableIdentity
    ? `${tableIdentity}:${gameNo}`
    : hand.handId;
};

export const findSelectedCompletedHandIndex = (
  hands: HandHistorySnapshot[],
  selectedHandKey?: string
) => {
  if (!selectedHandKey) return 0;

  const selectedIndex = hands.findIndex(
    (hand) => getCompletedHandSelectionKey(hand) === selectedHandKey
  );
  return selectedIndex >= 0 ? selectedIndex : 0;
};

export const parseHandHistoryCdnDocument = (
  document: any,
  {
    gameVariant,
    limit,
    sessionStartedAt,
    tableId
  }: {
    gameVariant: 'texas' | 'omaha';
    limit: number;
    sessionStartedAt: number;
    tableId: string;
  }
): HandHistoryResponse => {
  if (Number(document?.schemaVersion) !== 1) {
    throw new Error('Unsupported CDN hand-history schema');
  }
  if (String(document?.tableId) !== tableId || document?.gameVariant !== gameVariant) {
    throw new Error('CDN hand-history document identity mismatch');
  }

  const completedHands = (Array.isArray(document?.completedHands) ? document.completedHands : [])
    .filter((hand: HandHistorySnapshot) => Number(hand?.completedAt) >= sessionStartedAt)
    .sort(compareCompletedHandsNewestFirst)
    .slice(0, limit);

  return { locked: false, completedHands, storageSource: 'cdn-json' };
};

const handHistoryEventKey = (event: HandHistoryEvent) =>
  JSON.stringify([
    event.type,
    event.text,
    event.ts ?? null,
    event.totalPot ?? null,
    event.tableCards ?? null
  ]);

export const mergeActiveHandHistory = (
  cachedHand: ActiveHand | undefined,
  liveHand: ActiveHand | undefined,
  currentGameNo: number | string | undefined
): ActiveHand | undefined => {
  const gameNo = Number(currentGameNo);
  const cachedMatches = cachedHand && Number(cachedHand.gameNo) === gameNo;
  const liveMatches = liveHand && Number(liveHand.gameNo) === gameNo;

  if (!cachedMatches) return liveMatches ? liveHand : undefined;
  if (!liveMatches) return cachedHand;

  // The two sources overlap, but either can legitimately repeat an identical
  // row (same text, second, pot and board). Keep each row as many times as the
  // source that holds it most often, rather than once.
  const countByKey = (events: HandHistoryEvent[]) =>
    events.reduce((counts, event) => {
      const key = handHistoryEventKey(event);
      return counts.set(key, (counts.get(key) || 0) + 1);
    }, new Map<string, number>());
  const cachedCounts = countByKey(cachedHand.playByPlay);
  const liveCounts = countByKey(liveHand.playByPlay);
  const emitted = new Map<string, number>();
  const playByPlay = [...cachedHand.playByPlay, ...liveHand.playByPlay]
    .filter((event) => {
      const key = handHistoryEventKey(event);
      const count = emitted.get(key) || 0;
      if (count >= Math.max(cachedCounts.get(key) || 0, liveCounts.get(key) || 0)) return false;
      emitted.set(key, count + 1);
      return true;
    })
    .map((event, index) => ({ ...event, order: index + 1 }));

  return {
    ...cachedHand,
    ...liveHand,
    playByPlay
  };
};

export const archiveActiveHandHistory = (
  archivedHands: ActiveHand[] = [],
  activeHand?: ActiveHand,
  limit = 20
) => {
  if (!activeHand || !Number.isFinite(Number(activeHand.gameNo))) return archivedHands;

  const byGameNo = new Map<number, ActiveHand>();
  archivedHands.forEach((hand) => {
    if (Number.isFinite(Number(hand.gameNo))) byGameNo.set(Number(hand.gameNo), hand);
  });

  const gameNo = Number(activeHand.gameNo);
  byGameNo.set(
    gameNo,
    mergeActiveHandHistory(byGameNo.get(gameNo), activeHand, gameNo) || activeHand
  );

  return Array.from(byGameNo.values())
    .sort((a, b) => Number(a.gameNo) - Number(b.gameNo))
    .slice(-limit);
};

type HandHistoryPageCache = {
  response: HandHistoryResponse;
  activeHands: ActiveHand[];
  completedThroughGameNo?: number;
};

const normalizeHandHistoryPageCache = (
  stored: HandHistoryPageCache | HandHistoryResponse | undefined
): HandHistoryPageCache => {
  if (stored && 'response' in stored && Array.isArray(stored.activeHands)) return stored;

  const response =
    stored && !('response' in stored) && Array.isArray(stored.completedHands)
      ? stored
      : liveEmptyHandHistory;
  return {
    response,
    activeHands: response.activeHand ? [response.activeHand] : []
  };
};

export const mergeCompletedHandHistory = (
  cachedHands: HandHistorySnapshot[] = [],
  liveHands: HandHistorySnapshot[] = []
) => {
  const handsById = new Map<string, HandHistorySnapshot>();
  const hasAuthoritativeLiveIdentity = liveHands.some((hand) => Number(hand?.gameNo) > 0);
  const eligibleCachedHands = hasAuthoritativeLiveIdentity
    ? cachedHands.filter((hand) => Number(hand?.gameNo) > 0)
    : cachedHands;

  [...eligibleCachedHands, ...liveHands].forEach((hand) => {
    if (!hand?.handId) return;
    handsById.set(getCompletedHandSelectionKey(hand), hand);
  });

  return sortCompletedHandsNewestFirst(Array.from(handsById.values()));
};

const hasDealtHandHistoryCards = (game: any, seatId: number) =>
  [1, 2, 3, 4, 5].filter((card) =>
    /^(?:[2-9]|10|[AKQJ])[CDHS]$/.test(String(game[`p${seatId}card${card}`] || ''))
  ).length >= 2;

export const isLocallyQualifiedForHandHistory = ({
  game,
  seatId
}: {
  game: any;
  seatId: number;
}) => {
  const phase = Number(game.hand);
  if (
    !Number.isInteger(seatId) ||
    seatId <= 0 ||
    game.hand === '' ||
    !Number.isFinite(phase) ||
    phase < GAME_HAND_PHASE.SETUP_BIG_BLIND ||
    phase >= GAME_HAND_PHASE.RECORD_NEW_STATS_AND_START_NEW_HAND
  )
    return false;
  const status = Number(game[`p${seatId}status`]);
  if (
    ![PLAYER_STATUS.ACTIVE, PLAYER_STATUS.SHOW_CARDS, PLAYER_STATUS.MUCK_CARDS].includes(status)
  ) {
    return false;
  }
  const hasCards = hasDealtHandHistoryCards(game, seatId);
  if (String(game.tabletype || '').toLowerCase() !== 's') {
    return phase >= GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND && hasCards;
  }

  const bet = Number(game[`p${seatId}bet`]);
  const ante = Number(game[`p${seatId}ante`] || 0);
  const action = String(game[`p${seatId}action`] || '').toLowerCase();
  const blindAction = ['sb', 'bb'].includes(action);
  // Before dealing, a numeric contribution above the ante proves a blind was
  // deducted, including an out-of-position post or a short all-in blind.
  if (
    bet > ante &&
    (phase <= GAME_HAND_PHASE.DEAL_CARDS ||
      blindAction ||
      (phase === GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND && !action))
  )
    return true;
  // Recover when a broadcast/reconnect skips the posting update. Position alone
  // is insufficient: cards prove this seat participated in that same hand.
  return (
    hasCards &&
    ((Number(game.sbPlayer) === seatId && phase >= GAME_HAND_PHASE.SETUP_BIG_BLIND) ||
      (Number(game.bbPlayer) === seatId && phase >= GAME_HAND_PHASE.DEAL_CARDS))
  );
};

export const isHandHistoryAccessLocked = ({
  localAccessUnlocked
}: {
  localAccessUnlocked: boolean;
}) => !localAccessUnlocked;

const useHandHistoryTableState = (tableId?: string) => {
  const tableHistory = useTableSelector((table) => table.tableHistory || [], tableId);
  const game = useTableSelector((table) => table.game || {}, tableId);
  const isTournamentFinished = useTableSelector((table) => {
    const game = table.game;
    if (!game?.tabletype || game.tabletype === 's') return false;
    if (game.status === 'F' || game.status === 'C') return true;
    return Boolean(
      game.tournamentId &&
        Array.isArray(table.tournamentStatus) &&
        table.tournamentStatus.some(
          (status: any) =>
            String(status.tournamentId) === String(game.tournamentId) &&
            (status.tStatus === 'F' || status.tStatus === 'C')
        )
    );
  }, tableId);
  const isSeated = useTableSelector((table) => Boolean(table.currentPlayer?.isSeated), tableId);
  const playerGUID = useTableSelector(
    (table) => String(table.currentPlayer?.playerGUID || ''),
    tableId
  );
  const seatId = useTableSelector((table) => Number(table.currentPlayer?.seatId), tableId);
  const hydratedTableId = getRequestedTableId(game);
  const requestedTableId = tableId || hydratedTableId || '';
  const currentGameNo = game?.gameNo;
  const activeHandKey = getActiveHandKey(requestedTableId, currentGameNo);
  const playerStatus =
    Number.isFinite(seatId) && seatId > 0 ? Number(game?.[`p${seatId}status`]) : undefined;
  return {
    activeHandKey,
    currentGameNo,
    game,
    isSeated,
    isTournamentFinished,
    isTableHydrated: Boolean(hydratedTableId && hydratedTableId === requestedTableId),
    playerGUID,
    seatId,
    playerStatus,
    requestedTableId,
    tableHistory
  };
};

export const useLiveActiveHandHistory = ({
  activeHandKey,
  cachedHand,
  completedThroughGameNo = 0,
  currentGameNo,
  game,
  requestedTableId,
  source,
  tableHistory
}: {
  activeHandKey: string;
  cachedHand?: ActiveHand;
  completedThroughGameNo?: number;
  currentGameNo: number | string | undefined;
  game: any;
  requestedTableId: string;
  source: HandHistorySource;
  tableHistory: any[];
}) => {
  const activeHistoryBaselineRef = useRef({ key: '', index: 0 });
  const liveActiveHandRef = useRef<{ key: string; hand: ActiveHand } | null>(null);
  const settledBoardKeyRef = useRef('');
  const openingRef = useRef<{ gameNo: number; row: any } | null>(null);
  const previousTableHistoryRef = useRef<any[]>([]);
  const previousTableHistory = previousTableHistoryRef.current;

  if (activeHistoryBaselineRef.current.key !== activeHandKey) {
    activeHistoryBaselineRef.current = getNextActiveHistoryBaseline(
      activeHistoryBaselineRef.current,
      activeHandKey,
      previousTableHistory.length
    );
  }

  if (previousTableHistory !== tableHistory) {
    // The reducer keeps only the last 100 rows. Retained rows preserve their
    // identity, so move the hand boundary back by the number evicted from the
    // front. A length-only boundary stays at 100 and hides every later action.
    const retainedStart = previousTableHistory.indexOf(tableHistory[0]);
    const removedCount = retainedStart < 0 ? previousTableHistory.length : retainedStart;
    activeHistoryBaselineRef.current = {
      ...activeHistoryBaselineRef.current,
      index: Math.max(0, activeHistoryBaselineRef.current.index - removedCount)
    };
    previousTableHistoryRef.current = tableHistory;
  }

  const liveActiveHand = useMemo<ActiveHand | undefined>(() => {
    if (source !== 'live' || !requestedTableId) return undefined;
    const gameNo = Number(currentGameNo);
    const isStartingRow = (row: any) =>
      row?.languageKey === 'GAME_STARTING' ||
      /^Game starting(?:\.{3}|…)?$/i.test(String(row?.msg || '').trim());
    const startingRow = [...tableHistory].reverse().find(isStartingRow);
    // Preparation belongs to the NEXT hand: the engine increments gameNo only
    // when assigning its dealer. The first hand has no preceding settlement to
    // supply a Game # marker, so retain its real opening event across that change.
    if (
      startingRow &&
      Number(game.hand) >= GAME_HAND_PHASE.GAME_PREP &&
      Number(game.hand) <= GAME_HAND_PHASE.SETUP_DEALER &&
      game.hand !== '' &&
      (game.languageKey === 'GAME_STARTING' || isStartingRow({ msg: game.msg }))
    ) {
      openingRef.current = { gameNo: gameNo + 1, row: startingRow };
    } else if (startingRow && gameNo === 1 && !openingRef.current) {
      openingRef.current = { gameNo, row: startingRow };
    }
    const opening = openingRef.current?.gameNo === gameNo ? openingRef.current : null;
    const openingIndex = opening ? tableHistory.indexOf(opening.row) : -1;
    let activeHistory = getCurrentHandHistoryEntries(
      tableHistory || [],
      requestedTableId,
      currentGameNo,
      openingIndex >= 0
        ? Math.min(openingIndex, activeHistoryBaselineRef.current.index)
        : activeHistoryBaselineRef.current.index
    );
    if (openingRef.current?.gameNo === gameNo + 1) {
      const nextOpeningIndex = activeHistory.indexOf(openingRef.current.row);
      if (nextOpeningIndex >= 0) activeHistory = activeHistory.slice(0, nextOpeningIndex);
    }
    if (opening) {
      const marker = `Game #${requestedTableId}-${gameNo}`;
      const hasMarker = activeHistory.some((row) => String(row.msg || '').trim() === marker);
      if (!hasMarker) {
        activeHistory = [
          { msg: marker, systemMsg: true, totalPot: 0 },
          ...(activeHistory.includes(opening.row) ? [] : [opening.row]),
          ...activeHistory
        ];
      }
    }
    const phase = Number(game.hand);
    const previousHand =
      liveActiveHandRef.current?.key === activeHandKey
        ? liveActiveHandRef.current.hand
        : Number(cachedHand?.gameNo) === gameNo
        ? cachedHand
        : undefined;
    // Payout can expose pre-dealt cards even when a hand ended before the river.
    // Freeze the last revealed street, including across late phase updates.
    const settled =
      gameNo <= completedThroughGameNo ||
      game.hand === '' ||
      (game.hand !== null && game.hand !== undefined && (phase <= 1 || phase >= 14)) ||
      game.languageKey === 'GAME_MSG_ALLFOLD';
    if (settled) settledBoardKeyRef.current = activeHandKey;
    const revealedCount = phase >= 10 ? 5 : phase >= 8 ? 4 : phase >= 6 ? 3 : 0;
    // A hand first observed (or reconnected) once settled has no earlier render
    // to freeze from; its last street row still carries the board it revealed.
    const lastStreetBoard = () => {
      const streetRow = [...activeHistory]
        .reverse()
        .find((row) => liveEventType(row) === 'street' && dealtCards(row.tableCards).length > 0);
      return streetRow ? dealtCards(streetRow.tableCards) : [];
    };
    const board =
      settledBoardKeyRef.current === activeHandKey
        ? previousHand?.board?.length
          ? previousHand.board
          : lastStreetBoard()
        : [game.card1, game.card2, game.card3, game.card4, game.card5]
            .slice(0, revealedCount)
            .map((card) => (card === null || card === undefined ? '' : String(card).trim()));
    const isOmaha = String(game.game || '')
      .toLowerCase()
      .includes('omaha');
    const tableType = String(game.tabletype || '').toLowerCase();

    const playByPlay = buildHandHistoryEvents(
      activeHistory,
      requestedTableId,
      currentGameNo,
      'active'
    );

    const runBoards = getLiveRunBoards(game);
    const nextActiveHand: ActiveHand = {
      ...(runBoards ? { boards: runBoards } : {}),
      handId: `${game.game || 'game'}-${requestedTableId}-${currentGameNo || 0}-active`,
      gameNo: Number(currentGameNo || 0),
      gameType: tableType === 's' ? 'cash' : 'tournament',
      gameVariant: isOmaha ? 'omaha' : 'texas',
      bettingStructure: isOmaha ? 'PL' : 'NL',
      metadata: {
        smallBlind: Number(game.sb || 0),
        bigBlind: Number(game.bb || 0),
        ...(game.level || game.blindLevel ? { level: Number(game.level || game.blindLevel) } : {}),
        ...(game.ante ? { ante: Number(game.ante) } : {})
      },
      board,
      playByPlay:
        activeHistory.length === 0 && liveActiveHandRef.current?.key === activeHandKey
          ? liveActiveHandRef.current.hand.playByPlay
          : playByPlay
    };
    liveActiveHandRef.current = { key: activeHandKey, hand: nextActiveHand };
    return nextActiveHand;
  }, [
    activeHandKey,
    cachedHand,
    completedThroughGameNo,
    currentGameNo,
    game,
    requestedTableId,
    source,
    tableHistory
  ]);

  return liveActiveHand;
};

export const useCompletedHandHistory = ({
  currentGameNo,
  firstGameNo,
  gameVariant,
  isOpen,
  playerGUID,
  requestedTableId,
  sessionStartedAt,
  sessionActive,
  source
}: {
  currentGameNo: number | string | undefined;
  firstGameNo?: number;
  gameVariant: 'texas' | 'omaha';
  isOpen: boolean;
  playerGUID: string;
  requestedTableId: string;
  sessionStartedAt: number;
  sessionActive: boolean;
  source: HandHistorySource;
}) => {
  const { socket } = useContext(DualSocketContext);
  const scope = `${source}:${requestedTableId}:${gameVariant}:${playerGUID}:${sessionStartedAt}:${sessionActive}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const [liveData, setLiveData] = useState<HandHistoryResponse>(liveEmptyHandHistory);
  const [dataScope, setDataScope] = useState(scope);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [hasLoaded, setHasLoaded] = useState(false);
  const requestSequenceRef = useRef(0);
  const latestRequestIdRef = useRef('');
  const staleRetryTimerRef = useRef<number | undefined>(undefined);
  const fallbackTimerRef = useRef<number | undefined>(undefined);
  const [revealedOwnCards, setRevealedOwnCards] = useState<Record<number, string[]>>({});
  const ownCardsRecovery = useMemo(
    () =>
      createOwnHandCardsRecovery(
        `own-cards-${requestedTableId}-${gameVariant}-${sessionStartedAt}`,
        (request) =>
          socket?.emit(GET_OWN_HAND_CARDS, {
            ...request,
            tableId: requestedTableId,
            gameId: requestedTableId,
            gameVariant
          }),
        (gameNo, cards) => {
          if (scopeRef.current === scope) {
            setRevealedOwnCards((previous) => ({ ...previous, [gameNo]: cards }));
          }
        }
      ),
    [requestedTableId, gameVariant, sessionStartedAt, socket, scope]
  );

  const revealOwnCards = useCallback(
    (gameNo: number, completedAt: number) => {
      if (source !== 'live' || !socket || !requestedTableId || revealedOwnCards[gameNo]) return;
      ownCardsRecovery.request(gameNo, completedAt);
    },
    [source, socket, requestedTableId, revealedOwnCards, ownCardsRecovery]
  );

  useEffect(() => {
    if (source !== 'live' || !socket) return;
    socket.on(GOT_OWN_HAND_CARDS, ownCardsRecovery.receive);
    socket.on('connect', ownCardsRecovery.reconnect);
    return () => {
      socket.off(GOT_OWN_HAND_CARDS, ownCardsRecovery.receive);
      socket.off('connect', ownCardsRecovery.reconnect);
      ownCardsRecovery.dispose();
    };
  }, [socket, source, ownCardsRecovery]);

  const refresh = useCallback(() => {
    if (source !== 'live' || !sessionActive || !requestedTableId || !playerGUID) {
      logHandHistoryDiagnostic('info', 'Completed-hands request skipped', {
        currentGameNo,
        hasPlayerGUID: Boolean(playerGUID),
        requestedTableId,
        sessionActive,
        source
      });
      return;
    }
    if (staleRetryTimerRef.current) window.clearTimeout(staleRetryTimerRef.current);

    const request = async (allowStaleRetry: boolean) => {
      window.clearTimeout(fallbackTimerRef.current);
      requestSequenceRef.current += 1;
      const sequence = requestSequenceRef.current;
      const requestId = `${requestedTableId}-${sessionStartedAt}-${sequence}`;
      latestRequestIdRef.current = requestId;
      setIsLoading(true);
      setError(undefined);
      const url = `${GLOBAL_DATA_CDN_URL}/hand-history/v2/${gameVariant}/${requestedTableId}.json`;
      logHandHistoryDiagnostic('info', 'Requesting authorized completed hands', {
        currentGameNo,
        gameVariant,
        limit: 20,
        requestId,
        sessionStartedAt,
        sessionStartedAtIso: new Date(sessionStartedAt).toISOString(),
        tableId: requestedTableId,
        url
      });

      try {
        const [response, grants] = await Promise.all([
          fetch(url, { cache: 'no-cache', credentials: 'omit' }),
          requestHandGrants(socket, {
            tableId: requestedTableId,
            gameVariant,
            requestId,
            sessionStartedAt,
            firstGameNo,
            limit: 20
          })
        ]);
        if (!response.ok) throw new Error(`CDN_HTTP_${response.status}`);
        const data = await decryptHistoryDocument(
          await response.json(),
          grants,
          requestedTableId,
          gameVariant,
          sessionStartedAt,
          firstGameNo
        );
        if (
          !data ||
          typeof data.locked !== 'boolean' ||
          !Array.isArray(data.completedHands) ||
          data.completedHands.some(
            (hand) =>
              !hand ||
              !Array.isArray(hand.potGroups) ||
              !hand.tableSnapshot ||
              !Array.isArray(hand.tableSnapshot.seats) ||
              String(hand.tableId || hand.gameId) !== requestedTableId ||
              hand.gameVariant !== gameVariant
          )
        ) {
          throw new Error('Invalid hand-history response');
        }
        if (sequence !== requestSequenceRef.current || scopeRef.current !== scope) return;

        setIsLoading(false);
        setHasLoaded(true);
        setLiveData(data);
        setDataScope(scope);
        logHandHistoryDiagnostic('info', 'Authorized completed-hands response received', {
          completedHandCount: data.completedHands.length,
          requestId,
          storageSource: data.storageSource,
          tableId: requestedTableId
        });

        const current = Number(currentGameNo);
        const newest = data.completedHands.reduce(
          (latest, hand) => Math.max(latest, Number(hand.gameNo || 0)),
          0
        );
        if (allowStaleRetry && Number.isFinite(current) && current > 0 && newest < current) {
          staleRetryTimerRef.current = window.setTimeout(() => {
            if (sequence === requestSequenceRef.current && scopeRef.current === scope)
              void request(false);
          }, 2500);
        }
      } catch (cdnError) {
        if (sequence !== requestSequenceRef.current || scopeRef.current !== scope) return;
        const reason = cdnError instanceof Error ? cdnError.message : 'CDN_READ_FAILED';
        logHandHistoryDiagnostic(
          'warn',
          'CDN read failed; falling back to authenticated WebSocket',
          {
            hasSocket: Boolean(socket),
            reason,
            requestId,
            tableId: requestedTableId,
            url
          }
        );

        if (!socket) {
          setIsLoading(false);
          setHasLoaded(true);
          setError('Could not load hand history.');
          return;
        }
        socket.emit(GET_HAND_HISTORY, {
          tableId: requestedTableId,
          gameId: requestedTableId,
          gameVariant,
          requestId,
          sessionStartedAt,
          firstGameNo,
          limit: 20
        });
        fallbackTimerRef.current = window.setTimeout(() => {
          if (sequence !== requestSequenceRef.current || scopeRef.current !== scope) return;
          logHandHistoryDiagnostic('warn', 'Authenticated WebSocket fallback timed out', {
            requestId,
            tableId: requestedTableId
          });
          setIsLoading(false);
          setHasLoaded(true);
          setError('Could not load hand history.');
        }, HAND_HISTORY_FALLBACK_TIMEOUT_MS);
      }
    };

    void request(true);
  }, [
    currentGameNo,
    firstGameNo,
    gameVariant,
    playerGUID,
    requestedTableId,
    sessionActive,
    sessionStartedAt,
    socket,
    source,
    scope
  ]);

  useEffect(() => {
    if (source !== 'live' || !socket) return;
    const handleReady = (payload: any) => {
      if (
        String(payload?.tableId) === requestedTableId &&
        payload?.gameVariant === gameVariant &&
        isOpen
      )
        refresh();
    };
    const handleReconnect = () => {
      if (isOpen) refresh();
    };
    socket.on('handHistoryReady', handleReady);
    socket.on('connect', handleReconnect);
    return () => {
      socket.off('handHistoryReady', handleReady);
      socket.off('connect', handleReconnect);
    };
  }, [socket, source, requestedTableId, gameVariant, isOpen, refresh]);

  useEffect(
    () => () => {
      requestSequenceRef.current += 1;
      if (staleRetryTimerRef.current) window.clearTimeout(staleRetryTimerRef.current);
      window.clearTimeout(fallbackTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (source !== 'live' || !socket) return undefined;

    const handleHandHistory = (payload: any) => {
      if (payload?.accessOnly) return;
      if (!sessionActive || scopeRef.current !== scope) return;
      if (!isMatchingHandHistoryResponse(payload, requestedTableId, latestRequestIdRef.current)) {
        const responseTableId = payload?.tableId || payload?.gameId;
        if (responseTableId && String(responseTableId) === requestedTableId) {
          logHandHistoryDiagnostic('warn', 'Ignoring stale completed-hands response', {
            expectedRequestId: latestRequestIdRef.current,
            requestId: payload?.requestId,
            tableId: requestedTableId
          });
        }
        return;
      }

      window.clearTimeout(fallbackTimerRef.current);
      setIsLoading(false);
      setHasLoaded(true);
      logHandHistoryDiagnostic('info', 'Completed-hands response received', {
        completedHandCount: payload?.data?.completedHands?.length ?? 0,
        error: payload?.error,
        requestId: payload?.requestId,
        success: Boolean(payload?.success),
        tableId: requestedTableId
      });
      if (!payload?.success) {
        setError(payload?.error || 'Could not load hand history.');
        return;
      }

      const data = payload.data;
      if (
        !data ||
        typeof data.locked !== 'boolean' ||
        !Array.isArray(data.completedHands) ||
        data.completedHands.some(
          (hand: HandHistorySnapshot) =>
            !hand ||
            String(hand.tableId || hand.gameId) !== requestedTableId ||
            hand.gameVariant !== gameVariant ||
            !Array.isArray(hand.potGroups) ||
            !Array.isArray(hand.tableSnapshot?.seats)
        )
      ) {
        setError('Could not load hand history.');
        return;
      }
      setError(undefined);
      setLiveData(data);
      setDataScope(scope);
    };

    socket.on(GOT_HAND_HISTORY, handleHandHistory);
    return () => socket.off(GOT_HAND_HISTORY, handleHandHistory);
  }, [requestedTableId, gameVariant, sessionActive, socket, source, scope]);

  useEffect(() => {
    if (source !== 'live') return;
    requestSequenceRef.current += 1;
    if (staleRetryTimerRef.current) window.clearTimeout(staleRetryTimerRef.current);
    window.clearTimeout(fallbackTimerRef.current);
    latestRequestIdRef.current = '';
    setLiveData(liveEmptyHandHistory);
    setDataScope(scope);
    setError(undefined);
    setIsLoading(false);
    setHasLoaded(false);
    setRevealedOwnCards({});
  }, [scope, source]);

  useEffect(() => {
    if (source !== 'live' || !sessionActive || !isOpen) return;
    refresh();
  }, [currentGameNo, isOpen, refresh, sessionActive, source]);

  const matchesScope = dataScope === scope;
  return {
    error: matchesScope ? error : undefined,
    hasLoaded: matchesScope && hasLoaded,
    isLoading: matchesScope && isLoading,
    liveData: matchesScope ? liveData : liveEmptyHandHistory,
    refresh,
    revealedOwnCards: matchesScope ? revealedOwnCards : {},
    revealOwnCards
  };
};

// The game fields the live Active card reads. Any other table update (timers,
// other seats' stacks) must not rebuild the card, the data or the context.
// Every field useLiveActiveHandHistory reads must be here, including those its
// helpers read: getLiveRunBoards needs `runs`, `multiRunCards` and `gameType`,
// and without them a run-out's boards were never built (EN-11110 test1 Hand 27).
const LIVE_HAND_GAME_FIELDS = [
  'ante',
  'bb',
  'blindLevel',
  'card1',
  'card2',
  'card3',
  'card4',
  'card5',
  'game',
  'gameType',
  'hand',
  'languageKey',
  'level',
  'msg',
  'multiRunCards',
  'runs',
  'sb',
  'tabletype'
];

const useGameFields = (game: any, fields: string[]) => {
  const pickedRef = useRef<Record<string, unknown>>({});
  if (fields.some((field) => pickedRef.current[field] !== game?.[field])) {
    pickedRef.current = fields.reduce<Record<string, unknown>>(
      (picked, field) => ({ ...picked, [field]: game?.[field] }),
      {}
    );
  }
  return pickedRef.current;
};

// The server grants at most 50 hands per read (retainedHistory.js); keep the
// merged list to that so a long session does not grow it without bound.
export const MAX_RETAINED_COMPLETED_HANDS = 50;

type HandHistoryProviderProps = {
  children: React.ReactNode;
  defaultOpen?: boolean;
  source?: HandHistorySource;
  tableId?: string;
};

export const HandHistoryProvider = ({
  children,
  defaultOpen = false,
  source = 'live',
  tableId
}: HandHistoryProviderProps) => {
  const [demoLocked, setDemoLocked] = useState(false);
  const [demoGameMode, setDemoGameMode] = useState<HandHistoryDemoGameMode>('cash');
  const [demoVariant, setDemoVariant] = useState<HandHistoryDemoVariant>('texas');
  const [selectedHandKey, setSelectedHandKey] = useState<string>();
  const mountedAtRef = useRef(Date.now());
  const [sessionStartedAt, setSessionStartedAt] = useState(mountedAtRef.current);
  const [sessionActive, setSessionActive] = useState(source !== 'live');
  const [sessionFrozen, setSessionFrozen] = useState(false);
  const [frozenThroughGameNo, setFrozenThroughGameNo] = useState<number>();
  const [firstGameNo, setFirstGameNo] = useState<number>();
  const {
    activeHandKey,
    currentGameNo,
    game,
    isSeated,
    isTournamentFinished,
    isTableHydrated,
    playerGUID,
    seatId,
    playerStatus,
    requestedTableId,
    tableHistory
  } = useHandHistoryTableState(tableId);

  useHandHistoryNativeCheckpoint(requestedTableId, playerGUID, source === 'live' && sessionActive);

  const [isOpen, setIsOpen] = useState(() =>
    source === 'live'
      ? readHandHistoryPanelOpen(requestedTableId, playerGUID) ?? defaultOpen
      : defaultOpen
  );
  // Reset on a table/player identity change. A keyed provider would remount
  // its children - the whole table tree - when the player's GUID resolves.
  const historyScope = `${requestedTableId}:${playerGUID}`;
  const [stateScope, setStateScope] = useState(historyScope);
  if (stateScope !== historyScope) {
    setStateScope(historyScope);
    setSelectedHandKey(undefined);
    setSessionStartedAt(Date.now());
    setSessionActive(source !== 'live');
    setSessionFrozen(false);
    setFrozenThroughGameNo(undefined);
    setFirstGameNo(undefined);
    setIsOpen(
      source === 'live'
        ? readHandHistoryPanelOpen(requestedTableId, playerGUID) ?? defaultOpen
        : defaultOpen
    );
  }

  useEffect(() => {
    if (source === 'live') persistHandHistoryPanelOpen(requestedTableId, playerGUID, isOpen);
  }, [isOpen, playerGUID, requestedTableId, source]);

  useEffect(() => {
    if (source !== 'live' || typeof window === 'undefined') return;
    clearLegacyHandHistoryStorage(window.localStorage, requestedTableId, playerGUID);
  }, [playerGUID, requestedTableId, source]);

  // Scalars only: the session effect must not re-run (and re-persist) on
  // every table frame, only when something it decides on changes.
  const participatedInHand =
    isLocallyQualifiedForHandHistory({ game, seatId }) ||
    (Number(game.hand) >= GAME_HAND_PHASE.PRE_FLOP_BETTING_ROUND &&
      Number(game.hand) < GAME_HAND_PHASE.RECORD_NEW_STATS_AND_START_NEW_HAND &&
      [PLAYER_STATUS.ACTIVE, PLAYER_STATUS.SHOW_CARDS, PLAYER_STATUS.MUCK_CARDS].includes(
        playerStatus!
      ) &&
      hasDealtHandHistoryCards(game, seatId));
  const tableType = game.tabletype;

  useEffect(() => {
    if (
      source !== 'live' ||
      !isTableHydrated ||
      !requestedTableId ||
      !playerGUID ||
      typeof window === 'undefined'
    ) {
      return;
    }

    if (isHandHistorySessionEndStatus(playerStatus)) {
      logHandHistoryDiagnostic('info', 'Page session ended by player status', {
        currentGameNo,
        playerStatus,
        tableId: requestedTableId
      });
      endHandHistorySession(requestedTableId, playerGUID);
      setSessionActive(false);
      setSessionFrozen(false);
      setFrozenThroughGameNo(undefined);
      setSessionStartedAt(Date.now());
      setFirstGameNo(undefined);
      return;
    }

    if (isHandHistorySessionExitStatus(playerStatus)) {
      pauseHandHistorySession(requestedTableId, playerGUID);
      setSessionActive(false);
      return;
    }

    const freezing = isHandHistorySessionFreezeStatus(playerStatus, tableType);
    if (freezing) freezeHandHistorySession(requestedTableId, playerGUID, Number(currentGameNo));
    const frozenAt = readHandHistoryFrozenThroughGameNo(requestedTableId, playerGUID);

    // Hydration and socket reconnection can temporarily remove the player's seat.
    // A standing observer or finished tournament still has their played hands,
    // including after refresh. Restoration never grants hands to a new observer.
    // Read-only: re-persisting would clear an explicit exit's return deadline,
    // which only a seated return may clear.
    if (!isSeated || !Number.isFinite(playerStatus)) {
      const storedStart = readHandHistorySessionStartedAt(requestedTableId, playerGUID);
      const storedFirst = readHandHistoryFirstGameNo(requestedTableId, playerGUID);
      if (
        storedStart &&
        storedFirst !== undefined &&
        !hasEndedHandHistorySession(requestedTableId, playerGUID)
      ) {
        setSessionStartedAt(storedStart);
        setFirstGameNo(storedFirst);
        setSessionActive(true);
        setSessionFrozen(frozenAt !== undefined);
        setFrozenThroughGameNo(frozenAt);
      } else if (hasEndedHandHistorySession(requestedTableId, playerGUID)) {
        setSessionActive(false);
        setFirstGameNo(undefined);
      }
      return;
    }

    // Re-entry after a bust is a new playing session. Ordinary reconnects keep
    // the frozen boundary and can still fetch the delayed bust-out hand.
    if (
      !freezing &&
      frozenAt !== undefined &&
      Number(currentGameNo) > frozenAt &&
      playerStatus === PLAYER_STATUS.ACTIVE
    )
      endHandHistorySession(requestedTableId, playerGUID);
    const sessionEnded = hasEndedHandHistorySession(requestedTableId, playerGUID);
    const storedSessionStartedAt = readHandHistorySessionStartedAt(requestedTableId, playerGUID);
    const startedAt = resolveHandHistorySessionStartedAt({
      now: Date.now(),
      sessionEnded,
      storedSessionStartedAt
    });

    if (sessionEnded || storedSessionStartedAt !== startedAt) {
      logHandHistoryDiagnostic('info', 'Page session boundary started', {
        currentGameNo,
        playerStatus,
        previousSessionStartedAt: storedSessionStartedAt,
        reason: sessionEnded ? 'RETURNED_AFTER_ENDED_OR_FROZEN_SESSION' : 'FIRST_ACTIVE_SEAT',
        sessionStartedAt: startedAt,
        tableId: requestedTableId
      });
      clearHandHistorySessionData(requestedTableId, playerGUID);
    }
    persistHandHistorySession(
      requestedTableId,
      playerGUID,
      startedAt,
      participatedInHand ? Number(currentGameNo) : undefined
    );
    // A hand is the player's the moment they are dealt cards in it (or post a
    // blind for it) - the rule the server grants by, since a hand's participants
    // are the seats that held hole cards. Cash tables used to wait for the
    // player's next blind, so a player already in the game after a refresh or a
    // return saw an empty panel for up to a full orbit (Jason, 2026-09-23).
    if (participatedInHand) {
      qualifyHandHistorySession(requestedTableId, playerGUID, Number(currentGameNo));
    }
    setFirstGameNo(readHandHistoryFirstGameNo(requestedTableId, playerGUID));
    const frozenThrough = readHandHistoryFrozenThroughGameNo(requestedTableId, playerGUID);
    setSessionFrozen(frozenThrough !== undefined);
    setFrozenThroughGameNo(frozenThrough);
    setSessionActive(true);
    setSessionStartedAt((current) => (current === startedAt ? current : startedAt));
  }, [
    isSeated,
    isTableHydrated,
    currentGameNo,
    participatedInHand,
    playerGUID,
    playerStatus,
    tableType,
    requestedTableId,
    sessionFrozen,
    source
  ]);

  const scenarioGameMode =
    demoVariant === 'texas' ? demoGameMode : ('cash' as HandHistoryDemoGameMode);
  const scenario = handHistoryScenarios[scenarioGameMode][demoVariant];
  const persistedPageCache = useMemo<HandHistoryPageCache>(() => {
    if (source !== 'live' || !requestedTableId || !playerGUID || typeof window === 'undefined') {
      return normalizeHandHistoryPageCache(undefined);
    }

    const stored = readHandHistorySessionData<HandHistoryPageCache | HandHistoryResponse>(
      requestedTableId,
      playerGUID
    );

    return normalizeHandHistoryPageCache(stored);
  }, [playerGUID, requestedTableId, sessionStartedAt, source]);
  const persistedLiveData = persistedPageCache.response;
  const activeHandArchiveRef = useRef<ActiveHand[]>([]);
  const activeHandArchiveKeyRef = useRef('');
  const activeHandArchiveKey = `${source}:${requestedTableId}:${playerGUID}:${sessionStartedAt}`;
  if (activeHandArchiveKeyRef.current !== activeHandArchiveKey) {
    activeHandArchiveKeyRef.current = activeHandArchiveKey;
    activeHandArchiveRef.current = persistedPageCache.activeHands;
  }
  const retainedCompletedRef = useRef<{ scope: string; hands: HandHistorySnapshot[] }>({
    scope: '',
    hands: []
  });
  if (retainedCompletedRef.current.scope !== activeHandArchiveKey) {
    retainedCompletedRef.current = {
      scope: activeHandArchiveKey,
      hands: persistedLiveData.completedHands
    };
  }
  const completedGameVariant = String(game.game || '')
    .toLowerCase()
    .includes('omaha')
    ? 'omaha'
    : 'texas';
  const { error, isLoading, liveData, refresh, revealedOwnCards, revealOwnCards } =
    useCompletedHandHistory({
      currentGameNo,
      firstGameNo,
      gameVariant: completedGameVariant,
      isOpen,
      playerGUID,
      requestedTableId,
      sessionStartedAt,
      sessionActive: sessionActive && firstGameNo !== undefined,
      source
    });

  const completionRef = useRef({ scope: '', gameNo: 0 });
  if (completionRef.current.scope !== activeHandArchiveKey) {
    completionRef.current = {
      scope: activeHandArchiveKey,
      gameNo: persistedPageCache.completedThroughGameNo || 0
    };
  }
  const terminalPhase =
    game.hand === '' ||
    Number(game.hand) === GAME_HAND_PHASE.RECORD_NEW_STATS_AND_START_NEW_HAND ||
    (game.hand !== null &&
      game.hand !== undefined &&
      Number(game.hand) >= GAME_HAND_PHASE.GAME_PREP &&
      Number(game.hand) <= GAME_HAND_PHASE.SETUP_DEALER);
  // Completion is monotonic within a session. Preparation can reuse gameNo,
  // stale table phases can arrive, and a later API read can lag an earlier one.
  completionRef.current.gameNo = Math.max(
    completionRef.current.gameNo,
    terminalPhase ? Number(currentGameNo) || 0 : 0,
    ...persistedLiveData.completedHands.map((hand) => Number(hand.gameNo) || 0),
    ...liveData.completedHands.map((hand) => Number(hand.gameNo) || 0)
  );
  const completedThroughGameNo = completionRef.current.gameNo;

  const liveHandGame = useGameFields(game, LIVE_HAND_GAME_FIELDS);
  const liveActiveHand = useLiveActiveHandHistory({
    activeHandKey,
    cachedHand: persistedLiveData.activeHand,
    completedThroughGameNo,
    currentGameNo,
    game: liveHandGame,
    requestedTableId,
    source,
    tableHistory
  });

  useEffect(() => {
    if (source !== 'live' || !liveActiveHand) return;
    activeHandArchiveRef.current = archiveActiveHandHistory(
      activeHandArchiveRef.current,
      liveActiveHand
    );
  }, [activeHandArchiveKey, liveActiveHand, source]);

  const data = useMemo<HandHistoryResponse>(() => {
    if (source === 'demo') {
      const demoData = demoLocked ? lockedHandHistoryFixture : scenario;
      return {
        ...demoData,
        completedHands: sortCompletedHandsNewestFirst(demoData.completedHands)
      };
    }
    if (!sessionActive) return liveEmptyHandHistory;

    const accessLocked = isHandHistoryAccessLocked({
      localAccessUnlocked: firstGameNo !== undefined
    });
    if (accessLocked) {
      return {
        locked: true,
        lockedReason: 'NOT_DEALT_IN',
        completedHands: []
      };
    }

    const activeHand = mergeActiveHandHistory(
      persistedLiveData.activeHand,
      liveActiveHand,
      currentGameNo
    );

    const excludedGameNos = readHandHistoryExcludedGameNos(requestedTableId, playerGUID);
    const completedHands = applyRevealedOwnCards(
      attachViewerSeatToCompletedHands(
        enrichCompletedHandPlayByPlay(
          mergeCompletedHandHistory(
            mergeCompletedHandHistory(
              persistedLiveData.completedHands,
              retainedCompletedRef.current.hands
            ),
            liveData.completedHands
          )
            .filter(
              (hand) =>
                Number(hand.gameNo) >= firstGameNo! &&
                !excludedGameNos.includes(Number(hand.gameNo)) &&
                (frozenThroughGameNo === undefined || Number(hand.gameNo) <= frozenThroughGameNo)
            )
            .slice(0, MAX_RETAINED_COMPLETED_HANDS),
          tableHistory,
          requestedTableId,
          activeHandArchiveRef.current
        ),
        playerGUID
      ),
      revealedOwnCards
    );

    // gameNo can remain unchanged during settlement and the idle period.
    // Keep the captured events in the archive for Completed. Clear the Active
    // board and logs together at completion while retaining the blinds.
    const handInProgress =
      game.hand !== '' &&
      game.hand !== null &&
      game.hand !== undefined &&
      Number(game.hand) >= GAME_HAND_PHASE.GAME_PREP &&
      Number(game.hand) < GAME_HAND_PHASE.RECORD_NEW_STATS_AND_START_NEW_HAND &&
      Number(currentGameNo) > completedThroughGameNo;

    return {
      ...liveData,
      locked: false,
      completedHands,
      activeHand:
        !isTournamentFinished &&
        !sessionFrozen &&
        isSeated &&
        activeHand &&
        !excludedGameNos.includes(Number(activeHand.gameNo))
          ? {
              ...activeHand,
              board: handInProgress ? activeHand.board : [],
              // Read off the felt each update, never from the page cache: a
              // run-out's boards end with the hand.
              boards: handInProgress ? liveActiveHand?.boards : undefined,
              playByPlay: handInProgress ? activeHand.playByPlay : []
            }
          : undefined
    };
  }, [
    currentGameNo,
    completedThroughGameNo,
    demoLocked,
    game.hand,
    isTournamentFinished,
    isSeated,
    frozenThroughGameNo,
    firstGameNo,
    liveActiveHand,
    liveData,
    persistedLiveData,
    playerGUID,
    revealedOwnCards,
    scenario,
    sessionActive,
    sessionFrozen,
    source,
    tableHistory,
    requestedTableId
  ]);

  useEffect(() => {
    if (
      source !== 'live' ||
      !sessionActive ||
      !requestedTableId ||
      !playerGUID ||
      typeof window === 'undefined'
    ) {
      return;
    }

    persistHandHistorySessionData(requestedTableId, playerGUID, {
      response: data,
      activeHands: activeHandArchiveRef.current,
      completedThroughGameNo
    } as HandHistoryPageCache);
    retainedCompletedRef.current.hands = data.completedHands;
  }, [data, completedThroughGameNo, playerGUID, requestedTableId, sessionActive, source]);

  const selectedIndex = useMemo(
    () => findSelectedCompletedHandIndex(data.completedHands, selectedHandKey),
    [data.completedHands, selectedHandKey]
  );
  const completedHandsRef = useRef(data.completedHands);
  const selectedIndexRef = useRef(selectedIndex);
  completedHandsRef.current = data.completedHands;
  selectedIndexRef.current = selectedIndex;

  const setSelectedIndex = useCallback<React.Dispatch<React.SetStateAction<number>>>((next) => {
    const hands = completedHandsRef.current;
    const requestedIndex = typeof next === 'function' ? next(selectedIndexRef.current) : next;
    const index = Math.max(0, Math.min(hands.length - 1, requestedIndex));
    const hand = hands[index];

    setSelectedHandKey(hand ? getCompletedHandSelectionKey(hand) : undefined);
  }, []);

  useEffect(() => {
    setSelectedHandKey(undefined);
  }, [demoGameMode, demoVariant, requestedTableId, sessionStartedAt, source]);

  const value = useMemo(
    () => ({
      data,
      tableScope: source === 'live' ? `${requestedTableId}:${playerGUID}` : undefined,
      isLoading,
      isPreparing: source === 'live' && !isTableHydrated,
      error,
      refresh,
      demoLocked,
      setDemoLocked,
      demoGameMode,
      setDemoGameMode,
      demoVariant,
      setDemoVariant,
      isOpen,
      setIsOpen,
      selectedIndex,
      setSelectedIndex,
      revealOwnCards
    }),
    [
      data,
      demoGameMode,
      demoLocked,
      demoVariant,
      error,
      isLoading,
      isOpen,
      refresh,
      isTableHydrated,
      source,
      requestedTableId,
      playerGUID,
      setIsOpen,
      revealOwnCards,
      selectedIndex
    ]
  );

  return <HandHistoryContext.Provider value={value}>{children}</HandHistoryContext.Provider>;
};
